import { useCallback, useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import Linked from 'shared-components/service/Linked'
import {
  BADGE_LOOKS,
  badgeEyebrow,
  badgeHoldersLine,
  badgeLeft,
  badgeLimitedAction,
  badgePopoverDetail,
  badgeProgressRows,
  badgeRarityLine,
  badgeScarcity,
  badgeStepAction,
  badgeStepsLine,
  memberGearHref,
  nextInGroup,
  utcDay,
  type BadgeLimitedAction,
  type BadgeStepAction,
} from 'lib/community/model'
import { discussionsHref, newDiscussionHref } from 'lib/community/discussions'
import { earlyOwnerClaimLine, type BadgeFacts } from 'lib/community/gamification'
import type { CommunityBadge, CommunityBadgeId, CommunityMemberResponse } from 'lib/community/types'
import BottomSheet, { trapTab } from './BottomSheet'
import OwnPicker from './OwnPicker'
import { useSignedOutReader } from './useSignedOutReader'
import styles from './Badges.module.css'
import { FloatingPanel, POPOVER_ZONE_ATTR, useDisclosureGroup } from './Popover'

/**
 * The badge popover (Member Hub 3a–3c): the group as eyebrow, the name, what the badge is FOR,
 * then either the earned facts (date, how many members hold a capped one, and — the member only —
 * the next badge in that group) or the way there (DealHunt's progress rows, each with the act
 * that fills it; a capped single-act badge gets the scarcity bar and one CTA).
 *
 * Click / tap / Enter / Space only — no hover or focus auto-open (that reversed 15ef01ee: a
 * popover with links in it cannot vanish when the pointer crosses the gap). × and Escape close
 * and hand focus back to the badge. Desktop: under the badge, never over the member's header;
 * small or touch screens: a bottom sheet over a backdrop. Rendered only while open, so the
 * shared-cached HTML carries each badge once; one open per surface (`useBadgeDisclosure`).
 *
 * Every figure is DealHunt's (`progress`, `detail`, the summary's caps and points); a number
 * DealHunt did not send is a line not drawn.
 */

export type KnownBadge = CommunityBadge & { id: CommunityBadgeId }

/** What a badge surface (Overview block, Badges page) knows besides the badge itself. */
export interface BadgeSurface {
  member: CommunityMemberResponse
  /** Every badge this build knows, earned or not (`knownBadges`) — "Next in <group>". */
  badges: KnownBadge[]
  facts: BadgeFacts
  foundingCap: number | null
  /** The member is looking at their own page — after hydration only (`useOwnMemberMe`). */
  own: boolean
  /** Own page: the Conversation Starter bonus waits for a reply (`conversationStarterPending`). */
  starterWaiting: boolean
  /** Own page: the badges that lead Closest to unlock, in order (`closestPinned`, from `/me`) —
   *  the same head the Up next card reads. [] for a visitor. */
  pinned: CommunityBadgeId[]
  trailingSlash: boolean
}

/** Small or touch screens get the bottom sheet. */
export const BADGE_SHEET_QUERY = '(max-width: 639.98px), (hover: none)'

const OTHERS_NOTE = 'Given by other readers. Detailed reviews with photos get the most.'
const STARTER_WAITING_NOTE = 'Given when someone else replies to one of your discussions.'

/** The act a row or a capped badge asks for, as a link or the owner picker in place. */
export const BadgeAction: React.VFC<{
  action: BadgeStepAction | BadgeLimitedAction
  surface: BadgeSurface
  id: string
  primary?: boolean
  /** Replaces the popover's own class — the Up next card's primary button. */
  className?: string
}> = ({ action, surface, id, primary = false, className }) => {
  const [picking, setPicking] = useState(false)
  const { trailingSlash } = surface
  const linkClass = className ?? (primary ? styles.popCta : styles.popAction)
  if (action === 'review') {
    return picking ? (
      <div className={styles.popPicker}>
        <OwnPicker id={`badge-own-${id}`} autoFocus compact />
      </div>
    ) : (
      <button type="button" className={linkClass} onClick={() => setPicking(true)}>
        {'Leave an owner review →'}
      </button>
    )
  }
  if (action === 'reply' || action === 'answer') {
    return (
      <Linked href={discussionsHref(trailingSlash, 'unanswered')} className={linkClass}>
        {action === 'answer' ? 'Answer a discussion →' : 'Find discussions waiting for a reply →'}
      </Linked>
    )
  }
  if (action === 'start') {
    return (
      <Linked href={newDiscussionHref({}, trailingSlash)} className={linkClass}>
        {'Start a discussion →'}
      </Linked>
    )
  }
  if (action === 'update') {
    return (
      <Linked
        href={`${memberGearHref(surface.member.handle, trailingSlash)}#reviews`}
        className={linkClass}
      >
        {'Update one of your reviews →'}
      </Linked>
    )
  }
  return <p className={styles.popNote}>{OTHERS_NOTE}</p>
}

export const BadgePopoverBody: React.VFC<{
  badge: KnownBadge
  surface: BadgeSurface
  titleId: string
}> = ({ badge, surface, titleId }) => {
  const look = BADGE_LOOKS[badge.id]
  const cap = surface.facts.caps[badge.id] ?? null
  const reward = surface.facts.rewards[badge.id] ?? null
  const rows = badgeProgressRows(badge)
  const limited = look.group === 'limited'
  const detail = badgePopoverDetail(badge, rows)
  const waiting = surface.starterWaiting && badge.id === 'conversation_starter' && !badge.earned
  const signedOut = useSignedOutReader()

  const head = (
    <>
      <p className={clsx(styles.popEyebrow, limited && styles.popEyebrowLimited)}>
        {badgeEyebrow(badge.id, cap)}
      </p>
      <p id={titleId} className={styles.popTitle}>
        {look.name}
      </p>
      <p className={styles.popText}>{look.meaning}</p>
    </>
  )

  if (badge.earned) {
    const day = utcDay(badge.earned_at, true)
    const next = surface.own ? nextInGroup(surface.badges, badge.id, surface.facts.caps) : null
    return (
      <>
        {head}
        <p className={styles.popEarned}>
          <b>{day ? `Earned ${day}` : 'Earned'}</b>
          {cap && <span>{badgeHoldersLine(cap)}</span>}
        </p>
        {detail && <p className={styles.popText}>{detail}</p>}
        {next && (
          <p className={styles.popNext}>
            {`Next in ${next.group}: `}
            <b>{next.name}</b>
            {` · ${next.value}`}
          </p>
        )}
      </>
    )
  }

  const steps = badgeStepsLine(rows)
  // Every number given: nobody can earn it any more, so no act is offered for it.
  const soldOut = cap !== null && badgeLeft(cap) === 0
  const act = surface.own && !soldOut
  const limitedAction = rows ? null : badgeLimitedAction(badge.id)
  const scarcity = !rows && cap ? badgeScarcity(cap) : null
  // Early Owner while unearned and spots remain (the summary's cap): a signed-out review counts.
  // …and only for a reader who is not signed in (the popover renders only while open, client side).
  const claimLine = signedOut && badge.id === 'founding_owner' ? earlyOwnerClaimLine(cap) : null
  return (
    <>
      {head}
      {(steps || reward) && (
        <p className={styles.popSummary}>
          {steps && <span>{steps}</span>}
          {reward && (
            <span className={styles.popReward}>{`+${reward.toLocaleString(
              'en-US'
            )} pts when earned`}</span>
          )}
        </p>
      )}
      {scarcity && (
        <div className={styles.popScarcity}>
          <p className={styles.popScarcityLine}>
            <b>{scarcity.left}</b>
            <span>{scarcity.given}</span>
          </p>
          <span className={styles.popScarcityBar} aria-hidden="true">
            <span className={styles.popScarcityFill} style={{ width: `${scarcity.pct}%` }} />
          </span>
        </div>
      )}
      {rows && (
        <ul className={styles.popRows}>
          {rows.map(row => (
            <li key={row.key} className={styles.popRow}>
              <span className={styles.popRowLine}>
                <span
                  className={clsx(styles.popMark, row.done && styles.popMarkDone)}
                  aria-hidden="true"
                >
                  {row.done ? '✓' : ''}
                </span>
                <span className={styles.popLabel}>{row.label}</span>
                <b>{row.value}</b>
              </span>
              {!row.done && (
                <span className={styles.popBar} aria-hidden="true">
                  <span className={styles.popFill} style={{ width: `${row.pct}%` }} />
                </span>
              )}
              {!row.done && act && (
                <BadgeAction action={badgeStepAction(row.key)} surface={surface} id={badge.id} />
              )}
            </li>
          ))}
        </ul>
      )}
      {/* DealHunt's line ("57 of 100 left") only when the summary's bar is not drawn: one fact,
          one read — two caches could disagree in one panel. */}
      {!rows && !scarcity && detail && <p className={styles.popText}>{detail}</p>}
      {claimLine && <p className={styles.popText}>{claimLine}</p>}
      {waiting && <p className={styles.popNote}>{STARTER_WAITING_NOTE}</p>}
      {!waiting && limitedAction && act && (
        <BadgeAction action={limitedAction} surface={surface} id={badge.id} primary />
      )}
      {rows && cap && <p className={styles.popFooter}>{badgeRarityLine(cap)}</p>}
    </>
  )
}

/** Re-exported: the trap moved to the shared sheet (`BottomSheet`), its tests import it here. */
export { trapTab }

/**
 * The panel around the body: under the badge on a pointer screen (`FloatingPanel`, `prefer`
 * below), a bottom sheet with a backdrop on a small or touch one. The × takes focus on open, so a
 * keyboard reader lands inside; it closes and returns focus to the badge (`onClose`).
 */
export const BadgePanel: React.FC<{
  id: string
  labelledBy: string
  anchor: React.RefObject<HTMLElement>
  onClose: () => void
  children: React.ReactNode
}> = ({ id, labelledBy, anchor, onClose, children }) => {
  // The panel mounts only after a press, so `window` is there; the sheet decision is made once.
  const [sheet] = useState(
    () =>
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia(BADGE_SHEET_QUERY).matches
  )
  const closeButton = useRef<HTMLButtonElement>(null)
  // After the frame: the floating panel is measured (and made visible) in a layout effect, and
  // a hidden element cannot take focus.
  useEffect(() => {
    const frame = window.requestAnimationFrame(() =>
      closeButton.current?.focus({ preventScroll: true })
    )
    return () => window.cancelAnimationFrame(frame)
  }, [])
  const close = (
    <button
      ref={closeButton}
      type="button"
      className={styles.popClose}
      aria-label="Close"
      onClick={onClose}
    >
      {'×'}
    </button>
  )
  if (sheet) {
    // The layer is a popover zone, so the group's outside-pointerdown never fires on it. Escape
    // stays the disclosure group's (its capture-phase focus return, c9338ddd); the × takes focus.
    return (
      <BottomSheet
        id={id}
        labelledBy={labelledBy}
        onClose={onClose}
        closeOnEscape={false}
        focusOnOpen={false}
        restoreFocus={false}
        layerAttrs={{ [POPOVER_ZONE_ATTR]: '' }}
      >
        {close}
        {children}
      </BottomSheet>
    )
  }
  return (
    <FloatingPanel
      id={id}
      labelledBy={labelledBy}
      anchor={anchor}
      prefer="below"
      className={styles.popPanel}
    >
      {close}
      {children}
    </FloatingPanel>
  )
}

/** A badge surface's disclosure group: one popover open at a time across the whole surface
 *  (Overview's Earned row and Closest cards share one; the Badges page one across its sections).
 *  Click-only — `toggle` is the press; the group still closes on Escape and an outside press. */
export function useBadgeDisclosure(): {
  openId: string | null
  toggle: (id: string) => void
  close: () => void
} {
  const { openId, group } = useDisclosureGroup()
  const toggle = useCallback((id: string) => group.click(id), [group])
  const close = useCallback(() => group.close(), [group])
  return { openId, toggle, close }
}
